import { styled } from 'css-literal-loader/styled';
import { navigate } from 'gatsby';
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { injectStripe } from 'react-stripe-elements';
import Layout from '@4c/layout';

import { allowEventPickup } from '../../config.json';
import AddressCard from '../components/AddressCard';
import AddressForm from '../components/AddressForm';
import Card from '../components/Card';
import Form from '../components/Form';
import Heading from '../components/Heading';
import OrderSummary from '../components/OrderSummary';
import PaymentType from '../components/PaymentType';
import Responsive from '../components/Responsive';
import ServerErrorSummary from '../components/ServerErrorSummary';
import { CardCVC, CardExpiry, CardNumber } from '../components/Stripe';
import TermsAndConditionsModal from '../components/TermsAndConditionsModal';
import paths from '../paths';
import reservationPaymentSchema, {
  SourceType,
} from '../schema/reservationPayment';
import withReservation from '../utils/Reservation';

import textStyles from '../styles/responsive.module.scss';
import utilsStyles from '../styles/utils.module.scss';

const messages = defineMessages({
  cardName: {
    defaultMessage: 'Name (as it appears on credit card)',
    id: 'checkout.payment.cardName',
  },
  cardNumber: {
    defaultMessage: 'Credit Card Number',
    id: 'checkout.payment.cardNumber',
  },
  expiry: {
    defaultMessage: 'Expiration Date',
    id: 'checkout.payment.expiry',
  },
  expiryMobile: {
    defaultMessage: 'Expires: MM/DD ',
    id: 'checkout.payment.expiryMobile',
  },
  cvc: {
    defaultMessage: 'CVC',
    id: 'checkout.payment.cvc',
  },
  cardDeclined: {
    id: 'payment.cardDeclined',
    defaultMessage: 'The provided credit card was declined',
  },
  expired_card: {
    id: 'payment.cardDeclined.expired_card',
    defaultMessage: 'The provided card is expired.',
  },
  incorrect_zip: {
    id: 'payment.cardDeclined.incorrect_zip',
    defaultMessage:
      'The provided zip code is not correct for this card. Please provide the correct billing address for the intended credit card.',
  },
  invalid_cvc: {
    id: 'payment.cardDeclined.invalid_cvc',
    defaultMessage:
      'The provided CVC number is incorrect. Please correct it below.',
  },
  incorrect_cvc: {
    id: 'payment.cardDeclined.incorrect_cvc',
    defaultMessage:
      'The provided CVC number is incorrect. Please correct it below.',
  },
  invalid_expiry_year: {
    id: 'payment.cardDeclined.invalid_expiry_year',
    defaultMessage:
      'The provided card expiration date is incorrect. Please correct it below.',
  },
  incorrect_number: {
    id: 'payment.cardDeclined.incorrect_number',
    defaultMessage: 'The provided credit card number is incorrect',
  },
  invalid_number: {
    id: 'payment.cardDeclined.invalid_number',
    defaultMessage: 'The provided credit card number is incorrect',
  },
  issuer_not_available: {
    id: 'payment.cardDeclined.issuer_not_available',
    defaultMessage:
      'Your credit card issuer could not be reached. Please try again or contact your issuer directly.',
  },
});

const SubmitButton = styled(Form.SubmitButton, null, "SubmitButton", require("./_payment-SubmitButton.module.scss"), "submit-button", "submitButton");

const InvoiceNote = styled(Card, null, "InvoiceNote", require("./_payment-InvoiceNote.module.scss"), "invoice-note", "invoiceNote");

const Hr = styled('hr', null, "Hr", require("./_payment-Hr.module.scss"), "hr", "hr");

const Section = styled('section', null, "Section", require("./_payment-Section.module.scss"), "section", "section");

const propSourceType = {};

class PaymentSegment extends React.Component {
  constructor(...args) {
    super(...args);

    this.state = {
      value: reservationPaymentSchema.default(),
    };
  }

  componentDidMount() {
    this.setupPaymentRequest();
  }

  componentDidUpdate(_, prevState) {
    if (this.state.hasServerError && !prevState.hasServerError) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  handleChange = value => {
    this.setState({ value });
  };

  handleShowTerms = e => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ showTerms: true });
  };

  handleHideTerms = () => {
    this.setState({ showTerms: false });
  };

  handleAddressEdit = () => {
    this.setState({ editAddress: true });
  };

  handleServerError = errors => {
    const declineCode = errors
      .map(({ code, meta }) => {
        const decline = meta && meta.find(m => m.decline_code);
        return decline
          ? decline.decline_code
          : (code || '').replace('invalid_card.', '');
      })
      .find(Boolean);

    const nextState = { hasServerError: true };
    if (declineCode) {
      nextState.serverError = messages[declineCode] || messages.cardDeclined;
    }

    this.setState(nextState);
  };

  async setupPaymentRequest() {
    const { cart } = this.props;

    const paymentRequest = this.props.stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Total',
        amount: cart.total,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    this.paymentRequest = paymentRequest;

    try {
      const canMakePayment =
        !!window.ApplePaySession && !!(await paymentRequest.canMakePayment());

      paymentRequest.on('token', async ({ complete, token }) => {
        const paid = await this.payReservation({
          stripeToken: token.id,
          quoteRequested: false,
        });

        complete(paid ? 'success' : 'fail');
        if (paid) navigate(paths.confirmation);
      });

      this.setState(s => ({
        canMakePayment,
        value: {
          ...s.value,
          sourceType: canMakePayment
            ? SourceType.PAYMENT_REQUEST
            : SourceType.CARD,
        },
      }));
    } catch (err) {
      console.error(err);
    }
  }

  submitForm = async formValue => {
    const { stripe, cart } = this.props;

    if (formValue.sourceType === SourceType.PAYMENT_REQUEST) {
      this.paymentRequest.show();
      return;
    }

    let { billingAddress } = formValue;
    if (formValue.useShippingAddressAsBillingAddress) {
      billingAddress = { ...cart.shippingAddress };
      delete billingAddress.attention;
    }
    // just in case
    delete billingAddress.eventPickup;

    const payment = {
      billingAddress,
      quoteRequested: formValue.sourceType === SourceType.ELECTRONIC_INVOICE,
    };

    if (!payment.quoteRequested) {
      let tokenResult;

      try {
        tokenResult = await stripe.createToken({
          name: billingAddress.name,
          address_line1: billingAddress.line1,
          address_line2: billingAddress.line2 || undefined,
          address_city: billingAddress.city,
          address_state: billingAddress.state,
          address_zip: billingAddress.postalCode,
          address_country: 'US',
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('createToken failed locally', e);
        return;
      }

      const { token, error } = tokenResult;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('createToken failed remotely', error);
        return;
      }

      payment.stripeToken = token.id;
    }

    if (await this.payReservation(payment)) {
      navigate(paths.confirmation);
    }
  };

  payReservation = async payment => {
    try {
      await this.props.cart.payReservation(payment);
    } catch (gqlError) {
      let errors = [];

      if (gqlError && gqlError.details)
        errors = [].concat(
          ...gqlError.details.map(d => d.upstream && d.upstream.errors),
        );

      // If the reservation was already paid, just move on,
      // likely the user submitted twice somehow
      if (!errors.find(e => e.code === 'invalid_reservation.is_paid')) {
        this.handleServerError(errors);
        return false;
      }
    }
    return true;
  };

  submitAddressChange = async ({ eventPickup, ...shippingAddress }) => {
    const { cart } = this.props;
    await cart.setReservationCart(
      shippingAddress,
      eventPickup ? 'EVENT_PICKUP' : 'STANDARD_SHIPPING',
    );
    this.setState({ editAddress: false });
  };

  render() {
    const { cart } = this.props;
    const {
      value,
      canMakePayment,
      showTerms,
      editAddress,
      hasServerError,
      serverError,
    } = this.state;

    const usePaymentRequest =
      canMakePayment && value.sourceType === SourceType.PAYMENT_REQUEST;

    const shippingHeading = (
      <Heading level="2">
        <FormattedMessage
          defaultMessage="Shipping Information"
          id="checkout.payment.shippingDetails"
        />
      </Heading>
    );
    if (editAddress) {
      return (
        <Section>
          {shippingHeading}

          <AddressForm
            isShipping
            email={cart.email}
            withEventPickup={allowEventPickup}
            defaultValue={cart.shippingAddress}
            submitForm={this.submitAddressChange}
          >
            <Layout as={SubmitButton} alignSelf="flex-start">
              <FormattedMessage
                defaultMessage="Update Address"
                id="checkout.shippingAddressChange.submit"
              />
            </Layout>
          </AddressForm>
        </Section>
      );
    }

    return (
      <>
        {hasServerError && (
          <Section>
            <ServerErrorSummary>
              {serverError && <FormattedMessage {...serverError} />}
            </ServerErrorSummary>
          </Section>
        )}
        <Responsive mdDown>
          <Section>
            <OrderSummary totalsOnly />
          </Section>
        </Responsive>
        <Responsive mdUp>
          <Section>
            {shippingHeading}
            <AddressCard
              email={cart.email}
              address={cart.shippingAddress}
              onEdit={this.handleAddressEdit}
            />
          </Section>
        </Responsive>
        <Responsive smDown>
          {isSmall => (
            <Form
              value={value}
              pad={5}
              debug
              focusFirstInvalidOnSubmit
              onChange={this.handleChange}
              submitForm={this.submitForm}
              schema={reservationPaymentSchema}
            >
              <Section>
                <Heading level="2" className={textStyles.textAlignCenterSm}>
                  <FormattedMessage
                    defaultMessage="Payment Information"
                    id="checkout.paymentInfo"
                  />
                </Heading>
                <Layout pad={5} direction="column">
                  <Form.Field
                    name="sourceType"
                    as={PaymentType}
                    canMakePayment={canMakePayment}
                    direction={isSmall ? 'column' : 'row'}
                  />
                  {value.sourceType === SourceType.ELECTRONIC_INVOICE && (
                    <InvoiceNote>
                      <Card.Body>
                        <p>
                          <FormattedMessage
                            defaultMessage="We'll send you an email with your cart summary and a forwardable link to complete the order with credit card or ACH payment. Your quote will be valid for 60 days."
                            id="payment.quoteRequested.message1"
                          />
                        </p>
                        <p>
                          <FormattedMessage
                            defaultMessage="Use this method if your organization requires a quote to approve purchase, or if payment will be provided by a separate purchasing team."
                            id="payment.quoteRequested.message2"
                          />
                        </p>
                      </Card.Body>
                    </InvoiceNote>
                  )}
                  {value.sourceType === SourceType.CARD && (
                    <>
                      <Layout
                        className={utilsStyles.flexWrapPad4}
                        direction={isSmall ? 'column' : 'row'}
                      >
                        <Layout.Block grow style={{ minWidth: 310 }}>
                          <Form.FieldGroup
                            name="cardName"
                            events="onBlur"
                            label={messages.cardName}
                          />
                        </Layout.Block>
                        <Layout.Block grow style={{ minWidth: 310 }}>
                          <Form.FieldGroup
                            noValidate
                            name="cardNumber"
                            as={CardNumber}
                            placeholder=" "
                            label={messages.cardNumber}
                          />
                        </Layout.Block>
                      </Layout>

                      <Layout
                        pad={4}
                        style={
                          isSmall
                            ? undefined
                            : { width: '50%', paddingRight: 10 }
                        }
                      >
                        <Layout.Block flex="1" style={{ minWidth: 220 }}>
                          <Form.FieldGroup
                            noValidate
                            name="expiration"
                            as={CardExpiry}
                            label={
                              isSmall ? messages.expiryMobile : messages.expiry
                            }
                          />
                        </Layout.Block>
                        <Layout.Block style={{ minWidth: 90 }}>
                          <Form.FieldGroup
                            noValidate
                            name="CVC"
                            as={CardCVC}
                            placeholder=" "
                            label={messages.cvc}
                          />
                        </Layout.Block>
                      </Layout>
                    </>
                  )}
                  {canMakePayment != null &&
                    value.sourceType !== SourceType.PAYMENT_REQUEST && (
                      <>
                        <div>
                          <Form.Label>
                            <FormattedMessage
                              defaultMessage="Billing address"
                              id="checkout.payment.billingAddress.label"
                            />
                          </Form.Label>
                          <Layout
                            pad={isSmall ? 4 : 5}
                            direction={isSmall ? 'column' : 'row'}
                          >
                            <Form.FieldGroup
                              inline
                              type="radio"
                              checkedValue={true} // eslint-disable-line
                              name="useShippingAddressAsBillingAddress"
                            >
                              <FormattedMessage
                                defaultMessage="Same as shipping"
                                id="checkout.paymentAddress.useShippingAddress"
                              />
                            </Form.FieldGroup>

                            <Form.FieldGroup
                              inline
                              type="radio"
                              checkedValue={false}
                              name="useShippingAddressAsBillingAddress"
                            >
                              <FormattedMessage
                                defaultMessage="Use a different address"
                                id="checkout.paymentAddress.useDifferentAddress"
                              />
                            </Form.FieldGroup>
                          </Layout>
                        </div>
                        {!value.useShippingAddressAsBillingAddress && (
                          <AddressForm
                            name="billingAddress"
                            email={cart.email}
                          />
                        )}
                      </>
                    )}
                </Layout>
              </Section>
              <Section>
                <Heading level="2" className={textStyles.textAlignCenterSm}>
                  <FormattedMessage
                    defaultMessage="Terms and Conditions"
                    id="checkout.termsAndConditions"
                  />
                </Heading>
                <Layout direction="column" pad={5} align="flex-start">
                  <Form.FieldGroup
                    large
                    withTooltip
                    tooltipTheme="danger"
                    name="acknowledgeTermsAndConditions"
                  >
                    <span>
                      <FormattedMessage
                        defaultMessage="I have reviewed and agreed to the {termsAndConditionsLink}"
                        id="checkout.termsAndConditions.acknowledgeTermsAndConditions"
                        values={{
                          termsAndConditionsLink: (
                            <a
                              href="#terms-and-conditions"
                              onClick={this.handleShowTerms}
                            >
                              <FormattedMessage
                                defaultMessage="Butterfly Master Terms and Conditions"
                                id="checkout.termsAndConditions.reviewTerms"
                              />
                            </a>
                          ),
                        }}
                      />
                    </span>
                  </Form.FieldGroup>
                  <Form.FieldGroup
                    large
                    withTooltip
                    tooltipTheme="danger"
                    name="acknowledgeLicensed"
                  >
                    <FormattedMessage
                      defaultMessage="I am a physician or other practitioner in good standing and licensed by the law of the State in which I practice to use or order the use of the device. Or, I am purchasing the device pursuant to a valid prescription or order from such a physician or practitioner for use in professional practice as authorized by law."
                      id="checkout.termsAndConditions.acknowledgeLicensed"
                    />
                  </Form.FieldGroup>
                  <Form.FieldGroup
                    large
                    withTooltip
                    tooltipTheme="danger"
                    name="acknowledgeRenewal"
                  >
                    <FormattedMessage
                      defaultMessage="I understand that user subscriptions will automatically renew at the end of their term unless they are cancelled"
                      id="checkout.termsAndConditions.acknowledgeRenewal"
                    />
                  </Form.FieldGroup>
                </Layout>
              </Section>
              <Hr />
              <Section>
                <div>
                  <em>
                    <small>
                      <FormattedMessage
                        defaultMessage="Caution: Federal law restricts this device to sale to or on the order of a licensed physician or other licensed practitioner."
                        id="checkout.caution"
                      />
                    </small>
                  </em>
                </div>
                {/* If  checking about the payment don't show until its done to avoid potential style splash */}
                {canMakePayment != null && (
                  <SubmitButton applePay={usePaymentRequest}>
                    {!usePaymentRequest && (
                      <FormattedMessage
                        defaultMessage="Complete Order"
                        id="checkout.submit"
                      />
                    )}
                  </SubmitButton>
                )}
              </Section>
              <Form.Field name="acknowledgeTermsAndConditions">
                {({ onChange }) => (
                  <TermsAndConditionsModal
                    show={showTerms}
                    onHide={this.handleHideTerms}
                    onAccept={() => onChange(true)}
                  />
                )}
              </Form.Field>
            </Form>
          )}
        </Responsive>
      </>
    );
  }
}

PaymentSegment.propSourceType = propSourceType;

export default injectStripe(withReservation(PaymentSegment));
